/**
 * Toggle primitive — design.md § Density Modes / Workspace mode toggle scale
 * and docs/ia/admin-test-edit.md § Polish 7.
 *
 * Semantics: "settings, not selections." Toggles communicate a binary state
 * the user is configuring — never a multi-select choice (which is what
 * checkboxes signal). The contract bans native checkboxes for the Test
 * Settings shuffle / show-result row precisely because of that semantic
 * difference.
 *
 * Visual contract (design.md):
 *   - Workspace track:  36 × 20 px (compressed from editorial 44 × 24).
 *   - Slider:           4px inset (so slider diameter = 12px when off).
 *   - On track colour:  `accent` (#0139B7).
 *   - Off track colour: `muted-2` (#9A9A95).
 *   - Slider colour:    `bg-card` (#FFFFFF).
 *   - Transition:       transition-fast (120ms ease-out) on slide.
 *   - Focus ring:       3px accent-l halo + 1px accent border (focus-visible).
 *
 * a11y: rendered as `role="switch"` per WAI-ARIA Authoring Practices, with
 * `aria-checked` reflecting state. The component is keyboard-toggleable
 * (Space/Enter) because it is rendered as a `<button>`.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ToggleProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'aria-checked'> {
  /** Whether the toggle is in the "on" state. */
  checked: boolean;
  /** Called with the new boolean state when the user activates the toggle. */
  onCheckedChange: (next: boolean) => void;
  /** Accessible label. Either pass this OR wrap the toggle next to a <label> via
   *  aria-labelledby. The visible label is the parent's concern; the toggle
   *  exposes only the switch affordance. */
  'aria-label'?: string;
  /** Optional visible label text rendered as a sibling — preferred for the
   *  Settings cluster where the contract wants `Shuffle questions` etc.
   *  next to each switch. */
  label?: ReactNode;
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  function Toggle(
    { checked, onCheckedChange, label, className, disabled, id, ...props },
    ref,
  ) {
    const switchEl = (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        id={id}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          // Track: 36×20 (w-9 h-5).
          'relative inline-flex h-5 w-9 shrink-0 items-center',
          'rounded-pill border border-line',
          // Focus ring uses accent-l per design.md focus contract.
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l',
          'transition-fast',
          'disabled:cursor-not-allowed disabled:opacity-60',
          checked ? 'bg-accent' : 'bg-muted-2',
          className,
        )}
        {...props}
      >
        {/* Slider — 12×12 (4px inset on the 20px track), bg-card. Translate
            16px (track-w 36 − slider-w 12 − 2×inset 4 = 16) on the on state. */}
        <span
          aria-hidden="true"
          className={cn(
            'inline-block h-3 w-3 rounded-pill bg-card shadow-sm',
            'transition-fast',
            checked ? 'translate-x-[20px]' : 'translate-x-1',
          )}
        />
      </button>
    );

    if (label === undefined) return switchEl;

    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex cursor-pointer items-center gap-3',
          'font-sans text-small text-ink',
          disabled ? 'opacity-60 cursor-not-allowed' : '',
        )}
      >
        {switchEl}
        <span>{label}</span>
      </label>
    );
  },
);
